/* tslint:disable */
import {Color} from "../drawingDataModels";
import {Mesh} from "three";
import * as THREE from 'three';
import {int} from "aws-sdk/clients/datapipeline";
import { v4 as uuidv4 } from 'uuid';

export class EllipsoidROI {
    static nextRoiNumber = 1;
    id: number | null;
    uuid: string;
    name: string;
    color: Color;
    selected: boolean;
    isActive: boolean;
    isElliptical: boolean;
    mesh: Mesh;
    hasJson: boolean;

    backendData: {
        position: THREE.Vector3,
        rotation: THREE.Vector3,
        a: number,
        b: number,
        c: number
    };

    positions: {
        _3d: THREE.Vector3,
        _2d: {
            dorsal: THREE.Vector3,
            sagittal: THREE.Vector3,
            coronal: THREE.Vector3,
        }
    };

    radii: {
        _3d: number,
        _2d: {
            dorsal: { x: number, y: number },
            sagittal: { x: number, y: number },
            coronal: { x: number, y: number }
        }
    }

    constructor(hasJSON = false, dbId = null) {
        this.id = dbId
        this.uuid = uuidv4();
        this.name = `Ellipsoid ROI ${EllipsoidROI.nextRoiNumber++}`;
        this.color = Color.generateRandomColor();
        this.selected = true;
        this.isActive = true;
        this.isElliptical = false;
        this.mesh = undefined;

        this.positions = {
            _3d: null,
            _2d: {
                dorsal: null,
                sagittal: null,
                coronal: null,
            }
        };

        this.radii = {
            _3d: null,
            _2d: {
                dorsal: {x: null, y: null},
                sagittal: {x: null, y: null},
                coronal: {x: null, y: null}
            }
        };
        this.hasJson = hasJSON;
    }

    copyData(serialized_data) {
        this.name = serialized_data.name;
        this.color = new Color(serialized_data.color.hex);
        this.isElliptical = serialized_data.is_elliptical;
    }

    serialize() {
        let serializedData = {
            name: this.name,
            color: this.color,
            is_elliptical: this.isElliptical,
            mesh: {}
        };
        if (this.mesh) {
            serializedData.mesh = {
                position: this.mesh.position,
                rotation: this.mesh.rotation,
                a: this.mesh.scale.x,
                b: this.mesh.scale.y,
                c: this.mesh.scale.z,
            }
        }
        return serializedData;
    }

    static getActualRadiusInLayer(roiCenterLayer, maxDistance, currentLayer, roiRadiusOnActiveAxis) {
        //Params:
        // roiCenterLayer: The layer where the ROI center is (value of x, y, or z depending on the view)
        // maxDistance: The distance from the furthest point from the center of the ROI.
        //                 i.e the furthest point on the surface of teh ROI from the center.
        // currentLayer: the layer we want the radius of the ROI in.
        // roiRadiusOnActiveAxis: The radius of the ROI in a given axis (x,y,or z depending on the view).

        // Idea:
        // Find a point on the ellipse where the distance of this point on the x axis is
        // equal to the difference between the center of the ellipse (ROI) and the selected position.
        // This difference is the x value of the point, where the y value is actually the radius we are
        // calculating. Solve the 2D ellipse equation for the point with only (y) unknown, and get the
        // actual radius at this position.

        let maxAllowedDistance = Math.abs(roiCenterLayer - maxDistance);
        let distanceBetweenROICenterAndCurrentLayer = Math.abs(roiCenterLayer - currentLayer);

        let b2 = roiRadiusOnActiveAxis * roiRadiusOnActiveAxis;
        let a2 = maxAllowedDistance * maxAllowedDistance;
        let x2 = distanceBetweenROICenterAndCurrentLayer * distanceBetweenROICenterAndCurrentLayer;

        let r2 = b2 * (a2 - x2) / a2;

        let r = Math.sqrt(r2);

        return r;
    }

    static resetCounter() {
        EllipsoidROI.nextRoiNumber = 1;
    }
}

export class RectangularROI {
    static nextRoiNumber = 1;
    id: Number;
    name: string;
    color: Color;
    isActive: boolean;
    autoGenerated: boolean;
    dimensions: { width: number, height: number }
    topLeft: THREE.Vector2;
    bottomRight: THREE.Vector2;

    //This represents the interface position on the Dorsal, Sagittal, or Coronal canvas.
    viewsPositions: {
        _2d: {
            dorsal: {
                topLeft: THREE.Vector3,
                bottomRight: THREE.Vector3
            },
            sagittal: {
                topLeft: THREE.Vector3,
                bottomRight: THREE.Vector3
            },
            coronal: {
                topLeft: THREE.Vector3,
                bottomRight: THREE.Vector3
            },
        }
    }

    constructor(startPos, color, dimensions = null, autoGenerated = false) {

        this.id = uuidv4();
        this.name = `Rectangular ROI ${RectangularROI.nextRoiNumber++}`;
        this.isActive = true;
        this.autoGenerated = autoGenerated;

        this.dimensions = dimensions;
        if (!this.dimensions) {
            this.dimensions = {width: 1, height: 1};
        }

        // this.color = Color.generateRandomColor();
        // The ROI's position within the interface (Dorsal, Sagittal, Coronal)
        this.viewsPositions = {
            _2d: {
                dorsal: {
                    topLeft: null,
                    bottomRight: null
                },
                sagittal: {
                    topLeft: null,
                    bottomRight: null
                },
                coronal: {
                    topLeft: null,
                    bottomRight: null
                },
            }
        };

        // this.topLeft = startPos;
        this.topLeft = new THREE.Vector2(
            startPos.x - dimensions.width / 2,
            startPos.y - dimensions.height / 2,
        );
        // console.log("constructor  Top left=>", this.topLeft);
        // if (this.autoGenerated) {
        //     let shift_x = -Math.floor(this.dimensions.width / 2);
        //     let shift_y = -Math.floor(this.dimensions.height / 2);
        //     console.log("shift x = ", shift_x, "shift y = ", shift_y);
        //     this.shift(shift_x,shift_y);
        //     // this.shift(-Math.floor(this.dimensions.width / 2), 0);
        // }

        // this.bottomRight = new THREE.Vector2(this.topLeft.x + this.dimensions.width, this.topLeft.y + this.dimensions.height);
        this.bottomRight = new THREE.Vector2(startPos.x + this.dimensions.width / 2, startPos.x + this.dimensions.height / 2);

        // this.update(this.topLeft, this.bottomRight);
    }

    // update(pt_1:THREE.Vector2, pt_2:THREE.Vector2){
    //     this.topLeft.x = Math.min(pt_1.x, pt_2.x);
    //     this.topLeft.y = Math.min(pt_1.y, pt_2.y);
    //
    //     this.bottomRight.x = Math.max(pt_1.x, pt_2.x);
    //     this.bottomRight.y = Math.max(pt_1.y, pt_2.y);
    //
    //     this.dimensions.width = this.bottomRight.x - this.topLeft.x;
    //     this.dimensions.height = this.bottomRight.y - this.topLeft.y;
    //
    //     // this.center.x = this.topLeft.x + Math.floor(this.dimensions.width/2)
    //     // this.center.y = this.topLeft.y + Math.floor(this.dimensions.height/2)
    // }

    shift(x_shift: int, y_shift: int) {
        this.topLeft.x += x_shift;
        this.topLeft.y += y_shift;

        // this.bottomRight.x += x_shift;
        // this.bottomRight.y += y_shift;

    }

    static resetCounter() {
        RectangularROI.nextRoiNumber = 1;
    }
}
